import { useMemo, useState } from "react";
import { useTenantOrganization } from "../../lib/api";
import { dealersInScope, indexOrg } from "../../lib/organization";
import HealthGroups from "./HealthGroups";
import HealthJobs from "./HealthJobs";
import HealthJourney from "./HealthJourney";
import HealthMachine from "./HealthMachine";
import HealthOverview from "./HealthOverview";
import HealthPreIfr from "./HealthPreIfr";
import { Page, PageHeader, Select } from "./primitives";

// Overview and Server cover the whole platform, so only the Super Admin gets them.
const TABS = [
  { key: "overview", label: "Overview", adminOnly: true },
  { key: "templates", label: "Templates" },
  { key: "groups", label: "Group creation" },
  { key: "jobs", label: "Background jobs" },
  // One brand's Pre-IFR journey: what Tata and the bot send us, and the templates and events we send.
  { key: "preifr", label: "Pre-IFR Agent", preIfr: true },
  { key: "machine", label: "Server", adminOnly: true },
];

// The API counts days on India time, the clock the workshops use, whatever the
// browser's own zone is.
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const istDay = (daysBack) =>
  new Date(Date.now() + IST_OFFSET_MS - daysBack * 86400000).toISOString().slice(0, 10);

// "Last 7 days" includes today, so it starts 6 days back. The end is left open,
// which the API reads as "up to now".
const RANGES = [
  { key: "1", label: "Today", from: () => istDay(0) },
  { key: "7", label: "Last 7 days", from: () => istDay(6) },
  { key: "30", label: "Last 30 days", from: () => istDay(29) },
  { key: "90", label: "Last 90 days", from: () => istDay(89) },
];

const ALL = "all";
const filter = "!h-[34px] !w-[170px] text-[13px]";

// The dealers and workshops a brand's organization lists, each dealer with its workshops.
function placesIn(org) {
  const workshops = [...new Set((org?.workshops || []).map((w) => String(w.workshop_location ?? "").trim()))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  return { dealers: dealersInScope(indexOrg(org), "all"), workshops };
}

// `admin`: the Super Admin's view of every brand, with `tenant` the brand picked at
// the top of their page. Otherwise a brand's own login: the API keeps it to the
// workshops its role reaches, and its organization lists only those.
export default function SystemHealth({ tenant, admin = true, preIfr = false }) {
  // The Pre-IFR tab is only for a brand that has switched the journey on (the Super Admin sees it for any).
  const tabs = (admin ? TABS : TABS.filter((t) => !t.adminOnly)).filter((t) => !t.preIfr || admin || preIfr);
  const [tab, setTab] = useState(tabs[0].key);
  const [rangeKey, setRangeKey] = useState("7");
  const [scopeAll, setScopeAll] = useState(false);
  const [dealerId, setDealerId] = useState(ALL);
  const [workshop, setWorkshop] = useState(ALL);
  // The brand picked at the top of the page decides what this screen shows. Picking
  // another one there narrows it to that brand again, even after "All brands".
  const [followed, setFollowed] = useState(tenant?.id);
  if (followed !== tenant?.id) {
    setFollowed(tenant?.id);
    setScopeAll(false);
    setDealerId(ALL);
    setWorkshop(ALL);
  }
  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[1];
  const tenantId = admin && !scopeAll && tenant ? tenant.id : null;

  // "All brands" has no one organization to pick dealers and workshops from.
  const hasPlaces = !admin || tenantId != null;
  const { data: org } = useTenantOrganization(admin ? tenantId : null, { enabled: hasPlaces });
  const { dealers, workshops } = useMemo(() => placesIn(hasPlaces ? org : null), [hasPlaces, org]);
  const dealer = dealers.find((d) => String(d.id) === dealerId);
  const workshopOptions = dealer ? dealer.locations : workshops;
  const place = workshopOptions.includes(workshop) ? workshop : null;
  const scope = {
    tenantId,
    locations: place || (dealer ? dealer.locations.join(",") : undefined),
  };

  const pickBrand = (value) => {
    setScopeAll(value === "all");
    setDealerId(ALL);
    setWorkshop(ALL);
  };
  const pickDealer = (value) => {
    setDealerId(value);
    setWorkshop(ALL);
  };
  // The server is one thing for every brand, so the Server tab has no brand or workshop to pick.
  const placeFilters = tab !== "machine";

  return (
    <Page maxWidth={1180}>
      <PageHeader title="System health" />

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB]">
        <div className="flex max-w-full gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className="-mb-px h-10 flex-none cursor-pointer whitespace-nowrap border-b-2 px-4 text-[14px] font-medium"
              style={{
                borderColor: tab === t.key ? "#2667B5" : "transparent",
                color: tab === t.key ? "#2667B5" : "#6B7280",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 pb-2">
          {admin && placeFilters && (
            <Select aria-label="Brand" className={filter} value={tenantId != null ? "one" : "all"} onChange={(e) => pickBrand(e.target.value)}>
              <option value="all">All brands</option>
              {tenant && <option value="one">{tenant.name}</option>}
            </Select>
          )}
          {placeFilters && dealers.length > 1 && (
            <Select aria-label="Dealer" className={filter} value={dealer ? dealerId : ALL} onChange={(e) => pickDealer(e.target.value)}>
              <option value={ALL}>All dealers</option>
              {dealers.map((d) => (
                <option key={d.id} value={String(d.id)}>{d.name}</option>
              ))}
            </Select>
          )}
          {placeFilters && workshopOptions.length > 1 && (
            <Select aria-label="Workshop" className={filter} value={place || ALL} onChange={(e) => setWorkshop(e.target.value)}>
              <option value={ALL}>All workshops</option>
              {workshopOptions.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </Select>
          )}
          <Select
            aria-label="Period"
            className="!h-[34px] !w-[150px] text-[13px]"
            value={rangeKey}
            onChange={(e) => setRangeKey(e.target.value)}
          >
            {RANGES.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </Select>
        </div>
      </div>

      {tab === "overview" && <HealthOverview scope={scope} range={range} openTab={setTab} />}
      {tab === "templates" && <HealthJourney scope={scope} range={range} />}
      {tab === "groups" && <HealthGroups scope={scope} range={range} />}
      {tab === "jobs" && <HealthJobs scope={scope} range={range} />}
      {tab === "preifr" && <HealthPreIfr scope={{ ...scope, needsBrand: admin && tenantId == null }} range={range} />}
      {tab === "machine" && <HealthMachine range={range} />}
    </Page>
  );
}
